import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  CreateTeamRequest,
  CreateTeamSchema,
  UpdateTeamSchema,
  TeamAdminViewDto,
  UpdateTeamRequest,
} from '@act/shared/data-transfer-objects';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from 'redux/store-hooks';
import { createTeamSuccess, updateTeamSuccess } from 'redux/slices/teamsSlice';
import { useToast } from 'react-components/app/ReusableToast';
import { useApiCall } from 'redux/utils/api-hook';
import { teamsRequests } from 'redux/requests/teams.requests';
import { useEffect, useMemo } from 'react';
import { FlexContainer, FormBuilder, TwoColumnDataGrid } from '@act/storybook';
import { AdminDrawerForm } from '../AdminDrawer';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';

export const FieldDiv = styled.div`
  align-self: stretch;
  min-width: 150px;
`;
export interface PhoneOption {
  label: string;
  value: string;
}
interface Props {
  setOpen: (value: boolean) => void;
  phoneOptions: PhoneOption[];
  isEdit: boolean;
  open: boolean;
  selectedTeam?: TeamAdminViewDto;
}

export const TeamCreateEdit = ({
  setOpen,
  phoneOptions,
  isEdit,
  open,
  selectedTeam,
}: Props) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const schema = isEdit ? UpdateTeamSchema : CreateTeamSchema;
  const { control, handleSubmit, formState, reset } = useForm<
    CreateTeamRequest | UpdateTeamRequest
  >({
    defaultValues: {
      name: selectedTeam?.name || '',
      internalPhoneNumberId: selectedTeam?.internalPhoneNumberId || '',
    },
    resolver: zodResolver(schema as any),
  });
  const [createTeamResult, , createTeam] = useApiCall(teamsRequests.createTeam);
  const [updateTeamResult, , updateTeam] = useApiCall(teamsRequests.updateTeam);

  const formConfig: FormConfig<CreateTeamRequest> = [
    {
      type: 'input',
      name: 'name',
      label: 'Name',
      input: 'text',
      props: {
        required: true,
      },
    },
    {
      type: 'input',
      name: 'internalPhoneNumberId',
      label: 'Phone Number',
      input: 'select',
      props: {
        options:
          phoneOptions?.map(option => ({
            key: option.value,
            label: option.label,
            value: option.value,
          })) || [],
        fullWidth: true,
        required: true,
      },
    },
  ];

  const onSubmit = (data: CreateTeamRequest | UpdateTeamRequest) => {
    if (isEdit) {
      updateTeam({
        params: { id: selectedTeam?.id },
        body: data as UpdateTeamRequest,
      });
    } else {
      createTeam({ body: data });
    }
  };

  useEffect(() => {
    if (createTeamResult || updateTeamResult) {
      if (createTeamResult) {
        dispatch(createTeamSuccess(createTeamResult));
        toast('Team created successfully', 'success');
      }
      if (updateTeamResult) {
        dispatch(updateTeamSuccess(updateTeamResult));
        toast('Team updated successfully', 'success');
      }
      setOpen(false);
    }
  }, [createTeamResult, updateTeamResult]);

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (selectedTeam) {
      reset({
        name: selectedTeam.name,
        internalPhoneNumberId: selectedTeam.internalPhoneNumberId,
      });
    }
  }, [selectedTeam]);

  const DrawerContent = useMemo(() => {
    return (
      <FlexContainer direction="column" gap={2}>
        <FormBuilder
          config={formConfig}
          control={control}
          formState={formState}
        />
      </FlexContainer>
    );
  }, [control, formConfig, formState.errors]);

  const teamMembers = useMemo(() => {
    return (
      isEdit && (
        <TwoColumnDataGrid
          title="Team members"
          data={
            selectedTeam?.users?.map(
              user =>
                `${user.firstName} ${user.lastName} ${
                  user.isManager ? '(Manager)' : ''
                }`
            ) || []
          }
          emptyDataMessage="No team members found. Add some to get started."
        />
      )
    );
  }, [selectedTeam]);

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Team` : `Add New Team`}
      onSubmit={handleSubmit(onSubmit)}
      children={DrawerContent}
      extraContent={teamMembers}
      setOpen={handleClose}
      open={open}
    />
  );
};
